"use client";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
    MapPin,
    Car,
    TramFront,
    Bike,
    Info,
} from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";

const translations = {
    hu: {
        pageTitle:
            "strike megközelítés – Budapest XIII. kerület",

        introduction:
            "könnyen elérhető, akár autóval, akár tömegközlekedéssel érkezel.",

        address:
            "cím:",

        directions:
            "gyors útvonaltervezés:",

        googleMaps:
            "Google Térkép",

        appleMaps:
            "Apple Térképek",

        entranceImageAlt:
            "a strike bejárata Budapest XIII. kerületében, a Pannónia utcában",

        entranceTitle:
            "ezt keresd kívülről",

        entranceDescription:
            "itt találod a strike bejáratát",

        byCar:
            "autóval",

        carDescription:
            "a strike Budapest XIII. kerületének könnyen megközelíthető részén található. a környező utcákban fizetős parkolóhelyek állnak rendelkezésre, emellett néhány perc sétára több parkolóház is található.",

        parkingCode:
            "parkoló kód: 0133",

        recommendedParking:
            "ajánlott parkolók:",

        westendParking:
            "Westend Parkolóház",

        lehelParking:
            "Lehel Csarnok Parkoló",

        streetParking:
            "Pannónia utca és a környező utcák közterületi parkolói",

        publicTransport:
            "tömegközlekedéssel",

        publicTransportDescription:
            "a strike néhány perc sétára található Budapest több fontos közlekedési csomópontjától.",

        tram:
            "villamos:",

        tramDetails: (
            <>
                4-es és 6-os villamos
                <br />
                Jászai Mari tér – 12 perc séta
            </>
        ),

        metro:
            "metró:",

        metroDetails: (
            <>
                M3 metró
                <br />
                Lehel tér – 9 perc séta
            </>
        ),

        nearbyPlace:
            "közeli hely:",

        nearbyPlaceDetails: (
            <>
                Westend Bevásárlóközpont
                <br />
                8 perc séta
            </>
        ),

        bus:
            "busz:",

        busDetails: (
            <>
                15-ös busz
                <br />
                Hegedűs Gyula utca – 1 perc séta
            </>
        ),

        trolleybus:
            "trolibusz:",

        trolleybusDetails: (
            <>
                75-ös trolibusz
                <br />
                Hegedűs Gyula utca – 1 perc séta
                <br />
                <br />
                76-os és 83-as trolibusz
                <br />
                Szent István park – 2 perc séta
            </>
        ),

        byBike:
            "kerékpárral",

        bikeDescription:
            "a legközelebbi MOL Bubi gyűjtőállomások a Szent István parknál és a Pannónia utcában találhatók, egyaránt 4 perc sétára.",

        importantInformation:
            "fontos információ",

        arrivalInformation:
            "kérünk, hogy legalább 15 perccel a lefoglalt időpont előtt érkezz meg, hogy legyen időd a bejelentkezésre és a játék rövid ismertetésére. a turnusok pontosan a meghirdetett időpontokban indulnak.",

        mapTitle:
            "Strike Budapest térkép",
    },

    en: {
        pageTitle:
            "getting to strike in Budapest",

        introduction:
            "strike is easy to reach whether you arrive by car or public transport.",

        address:
            "address:",

        directions:
            "quick directions:",

        googleMaps:
            "Google Maps",

        appleMaps:
            "Apple Maps",

        entranceImageAlt:
            "strike entrance on Pannónia utca in Budapest",

        entranceTitle:
            "this is what to look for",

        entranceDescription:
            "this is the entrance to Strike",

        byCar:
            "by car",

        carDescription:
            "strike is located in an easily accessible part of Budapest’s District XIII. paid street parking is available in the surrounding streets, and several car parks are only a few minutes away on foot.",

        parkingCode:
            "parking zone code: 0133",

        recommendedParking:
            "recommended parking:",

        westendParking:
            "Westend Car Park",

        lehelParking:
            "Lehel Market Hall Car Park",

        streetParking:
            "public street parking on Pannónia utca and the surrounding streets",

        publicTransport:
            "by public transport",

        publicTransportDescription:
            "strike is only a short walk from several major public transport hubs in Budapest.",

        tram:
            "tram:",

        tramDetails: (
            <>
                trams 4 and 6
                <br />
                Jászai Mari tér – 12-minute walk
            </>
        ),

        metro:
            "metro:",

        metroDetails: (
            <>
                M3 metro
                <br />
                Lehel tér – 9-minute walk
            </>
        ),

        nearbyPlace:
            "nearby landmark:",

        nearbyPlaceDetails: (
            <>
                Westend Shopping Centre
                <br />
                8-minute walk
            </>
        ),

        bus:
            "bus:",

        busDetails: (
            <>
                bus 15
                <br />
                Hegedűs Gyula utca – 1-minute walk
            </>
        ),

        trolleybus:
            "trolleybus:",

        trolleybusDetails: (
            <>
                trolleybus 75
                <br />
                Hegedűs Gyula utca – 1-minute walk
                <br />
                <br />
                trolleybuses 76 and 83
                <br />
                Szent István park – 2-minute walk
            </>
        ),

        byBike:
            "by bicycle",

        bikeDescription:
            "the nearest MOL Bubi docking stations are located at Szent István park and on Pannónia utca. both are a 4-minute walk from strike.",

        importantInformation:
            "important information",

        arrivalInformation:
            "please arrive at least 15 minutes before your reserved time to allow enough time for check-in and a short introduction to the game. sessions start exactly at the scheduled time.",

        mapTitle:
            "Map of Strike Budapest",
    },
};

export default function GettingHerePage() {
    const {
        language,
    } =
        useLanguage();

    const text =
        translations[language] ||
        translations.hu;

    const entranceImage =
        language === "en"
            ? "/v4/nyisd_az_ajtot_strike-en.webp"
            : "/v4/nyisd_az_ajtot_strike.webp";

    const mapLanguage =
        language === "en"
            ? "en"
            : "hu";

    const mapSrc =
        `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d86193.5826349748!2d19.060947119610827!3d47.53763389603159!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4741ddadaf6cc26b%3A0x1232f794ae6c864e!2sStrike!5e0!3m2!1s${mapLanguage}!2s${mapLanguage}!4v1789122232368!5m2!1s${mapLanguage}!2s${mapLanguage}`;

    return (
        <main className="min-h-screen overflow-x-hidden bg-black font-sans">
            <Header />

            {/* FŐ INFORMÁCIÓ */}
            <section className="bg-[#dedede] text-black">
                <div className="mx-auto flex w-full max-w-[430px] flex-col items-center px-7 pb-12 pt-12 text-center md:max-w-5xl md:px-12 md:pb-16 md:pt-16">
                    <IconCircle>
                        <MapPin
                            style={{
                                width:
                                    "42px",

                                height:
                                    "42px",
                            }}
                            strokeWidth={
                                2.5
                            }
                        />
                    </IconCircle>

                    <h1 className="text-[25px] font-black lowercase leading-none md:text-[48px]">
                        {text.pageTitle}
                    </h1>

                    <p className="mt-8 max-w-[310px] text-[15px] leading-[1.15] text-neutral-700 md:max-w-xl md:text-[21px] md:leading-[1.35]">
                        {text.introduction}
                    </p>

                    <div className="mt-5 text-[15px] leading-[1.15] md:text-[21px]">
                        <strong className="block font-black">
                            {text.address}
                        </strong>

                        <span className="block">
                            1136 Budapest,
                        </span>

                        <span className="block">
                            Pannónia utca 46/A
                        </span>

                        <strong className="mt-6 block font-black">
                            {text.directions}
                        </strong>

                        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                            <a
                                href="https://maps.app.goo.gl/i3UfTiXguyVMBACb8"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cursor-pointer font-black underline underline-offset-4"
                            >
                                {text.googleMaps}
                            </a>

                            <a
                                href="https://waze.com/ul/hu2mw3d4y7"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cursor-pointer font-black underline underline-offset-4"
                            >
                                Waze
                            </a>

                            <a
                                href="https://maps.apple/p/Jm6zw6syetD24J"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cursor-pointer font-black underline underline-offset-4"
                            >
                                {text.appleMaps}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* HELYSZÍN KÍVÜLRŐL */}
            <section className="relative overflow-hidden bg-black">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage:
                            `url("${entranceImage}")`,

                        backgroundSize:
                            "cover",

                        backgroundPosition:
                            "center",

                        filter:
                            "blur(30px)",

                        transform:
                            "scale(1.15)",

                        opacity:
                            0.7,
                    }}
                />

                <div
                    className="relative mx-auto"
                    style={{
                        width:
                            "100%",

                        maxWidth:
                            "900px",
                    }}
                >
                    <img
                        src={
                            entranceImage
                        }
                        alt={
                            text.entranceImageAlt
                        }
                        className="block h-auto w-full"
                    />

                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent px-7 pb-7 pt-24 text-center text-white md:px-12 md:pb-12 md:pt-40">
                        <p className="text-[14px] font-medium lowercase tracking-[0.12em] text-white/75 md:text-[17px]">
                            Pannónia utca 46/A
                        </p>

                        <h2 className="mt-2 text-[26px] font-black lowercase leading-none md:text-[42px]">
                            {text.entranceTitle}
                        </h2>

                        <p className="mx-auto mt-3 max-w-[290px] text-[14px] leading-[1.3] text-white/80 md:max-w-xl md:text-[19px]">
                            {text.entranceDescription}
                        </p>
                    </div>
                </div>
            </section>

            {/* AUTÓVAL */}
            <TransportSection
                icon={
                    Car
                }
                title={
                    text.byCar
                }
            >
                <p className="mt-8 max-w-[290px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-2xl md:text-[21px] md:leading-[1.4]">
                    {text.carDescription}
                </p>

                <p className="mt-7 text-[17px] font-black md:text-[24px]">
                    {text.parkingCode}
                </p>

                <div className="mt-7 text-[15px] leading-[1.3] md:text-[20px] md:leading-[1.4]">
                    <strong className="block text-[17px] font-black md:text-[24px]">
                        {text.recommendedParking}
                    </strong>

                    <p className="mt-5 text-neutral-700">
                        {text.westendParking}
                        <br />
                        {text.lehelParking}
                        <br />
                        {text.streetParking}
                    </p>
                </div>
            </TransportSection>

            {/* TÖMEGKÖZLEKEDÉSSEL */}
            <TransportSection
                icon={
                    TramFront
                }
                title={
                    text.publicTransport
                }
            >
                <p className="mt-7 max-w-[290px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-2xl md:text-[21px] md:leading-[1.4]">
                    {text.publicTransportDescription}
                </p>

                <div className="mt-7 space-y-5 text-[15px] leading-[1.2] md:text-[20px]">
                    <TravelDetail
                        title={
                            text.tram
                        }
                    >
                        {text.tramDetails}
                    </TravelDetail>

                    <TravelDetail
                        title={
                            text.metro
                        }
                    >
                        {text.metroDetails}
                    </TravelDetail>

                    <TravelDetail
                        title={
                            text.nearbyPlace
                        }
                    >
                        {text.nearbyPlaceDetails}
                    </TravelDetail>

                    <TravelDetail
                        title={
                            text.bus
                        }
                    >
                        {text.busDetails}
                    </TravelDetail>

                    <TravelDetail
                        title={
                            text.trolleybus
                        }
                    >
                        {text.trolleybusDetails}
                    </TravelDetail>
                </div>
            </TransportSection>

            {/* KERÉKPÁRRAL */}
            <TransportSection
                icon={
                    Bike
                }
                title={
                    text.byBike
                }
                backgroundClass="bg-white"
                minimumHeightClass="min-h-[195px] md:min-h-[300px]"
            >
                <p className="mt-7 max-w-[300px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-2xl md:text-[21px] md:leading-[1.4]">
                    {text.bikeDescription}

                    <br />
                    <br />

                    <a
                        href="https://maps.app.goo.gl/pfKGASXghhLxXQvw5"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cursor-pointer font-black text-black underline underline-offset-4"
                    >
                        Szent István park
                    </a>

                    <br />

                    <a
                        href="https://maps.app.goo.gl/iP4E7pXHNcx1r6BM8"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cursor-pointer font-black text-black underline underline-offset-4"
                    >
                        Pannónia utca
                    </a>
                </p>
            </TransportSection>

            {/* FONTOS INFORMÁCIÓ */}
            <TransportSection
                icon={
                    Info
                }
                title={
                    text.importantInformation
                }
                minimumHeightClass="min-h-[245px] md:min-h-[350px]"
            >
                <p className="mt-7 max-w-[315px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-2xl md:text-[21px] md:leading-[1.4]">
                    {text.arrivalInformation}
                </p>
            </TransportSection>

            {/* TÉRKÉP */}
            <section className="bg-[#dedede]">
                <div className="mx-auto w-full max-w-[430px] md:max-w-[1440px]">
                    <div className="relative h-[285px] overflow-hidden bg-neutral-400 md:h-[620px]">
                        <iframe
                            key={
                                language
                            }
                            src={
                                mapSrc
                            }
                            className="h-full w-full border-0"
                            loading="lazy"
                            allowFullScreen
                            referrerPolicy="strict-origin-when-cross-origin"
                            title={
                                text.mapTitle
                            }
                        />
                    </div>

                    <div className="h-[58px] bg-[#dedede] md:h-[90px]" />
                </div>
            </section>

            <Footer />
        </main>
    );
}

function IconCircle({
    children,
}) {
    return (
        <div
            className="mb-6 flex shrink-0 items-center justify-center rounded-full border-[3px] border-black"
            style={{
                width:
                    "80px",

                height:
                    "80px",

                minWidth:
                    "80px",

                maxWidth:
                    "80px",

                minHeight:
                    "80px",

                maxHeight:
                    "80px",

                boxSizing:
                    "border-box",
            }}
        >
            {children}
        </div>
    );
}

function TransportSection({
    icon: Icon,
    title,
    children,
    backgroundClass = "bg-[#dedede]",
    minimumHeightClass = "min-h-[330px] md:min-h-[460px]",
}) {
    return (
        <section className={`${backgroundClass} text-black`}>
            <div
                className={`
                    mx-auto flex w-full max-w-[430px]
                    flex-col items-center justify-center
                    px-8 py-10 text-center
                    md:max-w-5xl md:px-12
                    ${minimumHeightClass}
                `}
            >
                <IconCircle>
                    <Icon
                        style={{
                            width:
                                "38px",

                            height:
                                "38px",
                        }}
                        strokeWidth={
                            2.5
                        }
                    />
                </IconCircle>

                <h2 className="text-[20px] font-black lowercase md:text-[34px]">
                    {title}
                </h2>

                {children}
            </div>
        </section>
    );
}

function TravelDetail({
    title,
    children,
}) {
    return (
        <div>
            <strong className="block text-[17px] font-black md:text-[24px]">
                {title}
            </strong>

            <p className="mt-1 text-neutral-700">
                {children}
            </p>
        </div>
    );
}